/**
 * Utility functions for proxy configuration
 * Handles parsing, building, and validating proxy URLs
 */

/**
 * Parse proxy URL into components
 * @param {string} url - Format: http://user:pass@host:port or http://host:port
 * @returns {object|null} - {host, port, username, password} or null if invalid
 */
export function parseProxyUrl(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    const urlObj = new URL(url);
    return {
      host: urlObj.hostname,
      port: urlObj.port || '8080',
      username: decodeURIComponent(urlObj.username) || '',
      password: decodeURIComponent(urlObj.password) || ''
    };
  } catch (e) {
    console.error('Failed to parse proxy URL:', e);
    return null;
  }
}

/**
 * Build proxy URL from components
 * @param {object} proxy - {host, port, username, password}
 * @returns {string|null} - Format: http://user:pass@host:port or null if incomplete
 */
export function buildProxyUrl(proxy) {
  if (!proxy || !proxy.host || !proxy.port) {
    return null;
  }

  // Build authentication part if both username and password are provided
  const auth = proxy.username && proxy.password
    ? `${encodeURIComponent(proxy.username)}:${encodeURIComponent(proxy.password)}@`
    : '';

  return `http://${auth}${proxy.host}:${proxy.port}`;
}

/**
 * Validate proxy configuration
 * @param {object} proxy - {host, port, username, password}
 * @returns {object} - {valid: boolean, errors: array}
 */
export function validateProxy(proxy) {
  const errors = [];

  // If any field is filled, host and port become required
  const hasAnyField = proxy.host || proxy.port || proxy.username || proxy.password;

  if (hasAnyField) {
    if (!proxy.host || proxy.host.trim() === '') {
      errors.push('Хост обязателен для заполнения');
    }

    if (!proxy.port || proxy.port.toString().trim() === '') {
      errors.push('Порт обязателен для заполнения');
    } else {
      const port = parseInt(proxy.port);
      if (isNaN(port) || port < 1 || port > 65535) {
        errors.push('Порт должен быть числом от 1 до 65535');
      }
    }

    // If username is provided, password should be too (and vice versa)
    if (proxy.username && !proxy.password) {
      errors.push('Если указано имя пользователя, необходимо указать пароль');
    }
    if (proxy.password && !proxy.username) {
      errors.push('Если указан пароль, необходимо указать имя пользователя');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Check if proxy configuration is empty
 * @param {object} proxy - {host, port, username, password}
 * @returns {boolean} - true if all fields are empty
 */
export function isProxyEmpty(proxy) {
  if (!proxy) return true;
  return !proxy.host && !proxy.port && !proxy.username && !proxy.password;
}

/**
 * Reset proxy object to empty state
 * @returns {object} - Empty proxy object
 */
export function createEmptyProxy() {
  return {
    host: '',
    port: '',
    username: '',
    password: ''
  };
}
